import { createElement } from 'react';
import { UsersRoundIcon } from 'lucide-react';

import { defineModule } from '@/shared/registry/types';

import { useGroupCounters } from './api';
import { groupCommandSources } from './commands';
import { GroupSearchResult } from './components/GroupSearchResult';
import { AboutTabEntry, GroupPanelEntry, MembersTabEntry, UserGroupsTabEntry } from './entries';
import { registerGroupRealtime } from './realtime';

/** Sidebar badge: invitations to answer + join requests to decide (kept live by realtime). */
function useGroupsCounter(): number | undefined {
  const counters = useGroupCounters().data;
  return counters ? counters.invitations + counters.joinRequests : undefined;
}

/**
 * `groups` module — workgroups and projects (docs/modules/groups.md): the list (`/groups`), group pages with the tabs
 * every module contributes (`/groups/:groupId/:tab`), the `group:<id>` side panel and the "Groups" profile tab.
 */
export const module = defineModule({
  id: 'groups',
  routes: [
    {
      path: 'groups',
      children: [
        { index: true, lazy: async () => ({ Component: (await import('./pages/GroupsListPage')).default }) },
        { path: ':groupId', lazy: async () => ({ Component: (await import('./pages/GroupPage')).default }) },
        {
          path: ':groupId/:tab',
          lazy: async () => ({ Component: (await import('./pages/GroupPage')).default }),
        },
      ],
    },
  ],
  nav: [
    {
      id: 'groups',
      labelKey: 'groups:nav.title',
      icon: UsersRoundIcon,
      to: '/groups',
      order: 60,
      section: 'main',
      useCounter: useGroupsCounter,
    },
  ],
  groupTabs: [
    {
      id: 'members',
      labelKey: 'groups:tabs.members',
      order: 90,
      element: (groupId) => createElement(MembersTabEntry, { groupId }),
    },
    {
      id: 'about',
      labelKey: 'groups:tabs.about',
      order: 100,
      element: (groupId) => createElement(AboutTabEntry, { groupId }),
    },
  ],
  profileTabs: [
    {
      id: 'groups',
      labelKey: 'groups:profile.tab',
      order: 30,
      element: (userId) => createElement(UserGroupsTabEntry, { userId }),
    },
  ],
  sidePanels: [{ type: 'group', component: GroupPanelEntry, size: 'md', titleKey: 'groups:panel.title' }],
  searchRenderers: { group: GroupSearchResult },
  commands: groupCommandSources,
  setup: ({ realtime, queryClient }) => {
    registerGroupRealtime(realtime, queryClient);
  },
});
